// @ts-nocheck
import { formGlobals } from './_/form-globals';
import BaseInput from './_/base-input';

/**
 * Class: NumberInput
 * Purpose: Manage a single <input type="number">, track numeric validity and emit custom events on changes.
 * Constructor Parameters (types):
 *   wrap (HTMLElement)      - Container element that wraps the input + error UI.
 *   formIndex (number|null) - Index of the parent form instance.
 *   wrapIndex (number|null) - Index of this field within the form instance.
 *
 * Public Methods:
 *   isValid(): boolean
 *     // Returns current validity state.
 *
 *   getState(): { value:string, numberValue:number|null, valid:boolean, blurred:boolean, required:boolean, id:string }
 *     // Returns current state including parsed numeric value (or null if unparsable/empty).
 *
 *   validateNow(): void
 *     // Forces validation against current raw value.
 *
 *   destroy(): void
 *     // Removes listeners.
 *
 * Events:
 *   'validchange' (detail:{ valid:boolean, value:string, numberValue:number|null, instance:NumberInput })
 *   'valuechange' (detail:{ value:string, numberValue:number|null, instance:NumberInput })
 *   'blur'        (detail:{ instance:NumberInput })
 *   'focus'       (detail:{ instance:NumberInput })
 *
 * Notes:
 * - Events bubble and are composed so parent controllers can listen without holding references.
 * - Reads native constraints (min/max/step) and optional data-validate rules.
 * - Supports decimal comma (e.g., "3,5" → 3.5).
 * 
 * @class
 * @extends {BaseInput}
 * @this {NumberInput}
 */
export default class NumberInput extends BaseInput {
  /**
   * @param {HTMLElement} wrap
   * @param {number|null} formIndex
   * @param {number|null} wrapIndex
   */
  constructor(wrap, formIndex = null, wrapIndex = null) {
    super(wrap, formIndex, wrapIndex);

    /** @type {HTMLInputElement|null} */
    this.input = wrap.querySelector('input[type="number"]');
    this.inputId = `form-${formIndex}-input-${wrapIndex}`;

    // Initialize base class elements
    this._initErrorElement();
    this._initNoteElement(this.wrap.querySelector(formGlobals.noteSelector) || null);

    // config
    this.required    = this.input?.getAttribute('aria-required') === 'true';
    this.validateKey = this.input?.getAttribute('data-validate') || null;

    // Native constraints (strings) → read raw; use as numbers where needed
    // Empty strings should be treated as null (no constraint)
    const minRaw = this.input?.getAttribute('min');
    const maxRaw = this.input?.getAttribute('max');
    const stepRaw = this.input?.getAttribute('step');
    
    this.minAttr  = (minRaw && minRaw.trim() !== '') ? minRaw : null;
    this.maxAttr  = (maxRaw && maxRaw.trim() !== '') ? maxRaw : null;
    this.stepAttr = (stepRaw && stepRaw.trim() !== '') ? stepRaw : null;

    // state
    this.blurred = false;
    this.value = this.input?.value ?? '';
    this.numberValue = NumberInput.parseNumber(this.value);
    this.valid = this.required ? false : true;

    // listeners cleanup
    this._onFocus = this._onFocus.bind(this);
    this._onBlur  = this._onBlur.bind(this);
    this._onInput = this._onInput.bind(this);
    this._onWheel = this._onWheel.bind(this);

    this._setAttributes();
    this._initEvents();
  }

  /** @returns {HTMLInputElement|null} */
  _getAriaElement() {
    return this.input;
  }

  _getValidChangeDetail(): { valid: boolean; value: string; numberValue: number | null; instance: NumberInput } {
    return {
      valid: this.valid,
      value: this.value,
      numberValue: this.numberValue,
      instance: this
    };
  }

  /** @returns {boolean} */
  isValid() {
    return this.valid;
  }

  /** @returns {{value:string, numberValue:number|null, valid:boolean, blurred:boolean, required:boolean, id:string}} */
  getState() {
    return {
      value: this.value,
      numberValue: this.numberValue,
      valid: this.valid,
      blurred: this.blurred,
      required: this.required,
      id: this.inputId
    };
  }

  /** Force validation against current value. */
  validateNow() {
    this._validate(this.value);
  }

  /** Remove listeners and references. */
  destroy() {
    super.destroy();
    this.input = null;
  }

  // ---------- Private ----------
  /**
   * Function: _setAttributes
   * Purpose: Initialize IDs and initial aria-describedby based on note/error presence.
   */
  _setAttributes() {
    if (!this.input) return;

    this.input.setAttribute('id', this.inputId);

    // inputmode hint for better mobile keyboard
    if (!this.input.hasAttribute('inputmode')) {
      this.input.setAttribute('inputmode', 'decimal');
    }

    // Initial describedby: note if present (no error initially)
    this._setAriaDescribedBy({ includeNote: true, includeError: false });
  }

  /**
   * Function: _initEvents
   * Purpose: Attach DOM listeners with AbortController for clean teardown.
   */
  _initEvents() {
    if (!this.input) return;
    const o = { signal: this._ac.signal, passive: true };

    this.input.addEventListener('focus', this._onFocus, o);
    this.input.addEventListener('blur',  this._onBlur,  o);
    this.input.addEventListener('input', this._onInput, { signal: this._ac.signal }); // not passive
    // prevent accidental value changes via mouse wheel when focused
    this.input.addEventListener('wheel', this._onWheel, { signal: this._ac.signal, passive: false });
  }

  _onWheel(e) { e.preventDefault(); }

  _onFocus() {
    this.wrap.classList.add(formGlobals.focusedClass);
    this._dispatch('focus', { instance: this });
  }

  _onBlur() {
    this.wrap.classList.remove(formGlobals.focusedClass);
    const wasFirst = !this.blurred;
    this.blurred = true;
    this._dispatch('blur', { instance: this });

    if (wasFirst && this.required) {
      this._validate(this.value);
    }
  }

  _onInput() {
    this.value = this.input.value;
    this.numberValue = NumberInput.parseNumber(this.value);

    if (this.value) {
      this.wrap.classList.add(formGlobals.filledClass);
    } else {
      this.wrap.classList.remove(formGlobals.filledClass);
    }

    this._dispatch('valuechange', { value: this.value, numberValue: this.numberValue, instance: this });

    if (this.blurred && (this.required || this.value !== '')) {
      this._validate(this.value);
    }
  }

  /**
   * Function: _validate
   * Purpose: Apply required rule, native constraints (min/max/step), and custom rules.
   * Params:
   *   rawValue (string) - current raw value
   */
  _validate(rawValue) {
    const n = NumberInput.parseNumber(rawValue);
    let isValid = true;

    // Required rule
    if (this.required) {
      isValid = rawValue.trim() !== '' && Number.isFinite(n);
    } else {
      if (rawValue.trim() === '') return this._setValid();
      if (!Number.isFinite(n)) return this._setInvalid();
    }
    if (!isValid) return this._setInvalid();

    // Native constraints
    // Note: empty strings are normalized to null during initialization
    if (this.minAttr !== null && Number.isFinite(+this.minAttr)) {
      if (n < +this.minAttr) isValid = false;
    }
    if (this.maxAttr !== null && Number.isFinite(+this.maxAttr)) {
      if (n > +this.maxAttr) isValid = false;
    }
    if (this.stepAttr && this.stepAttr !== 'any') {
      const step = NumberInput.parseNumber(this.stepAttr);
      if (Number.isFinite(step) && step > 0) {
        const base = this.minAttr !== null ? (NumberInput.parseNumber(this.minAttr) ?? 0) : 0;
        const diff = Math.abs((n - base) / step);
        if (Math.abs(diff - Math.round(diff)) > 1e-9) isValid = false; // FP tolerance
      }
    }

    // Data-validate rules
    if (isValid && this.validateKey) {
      const rules = NumberInput.parseValidateKey(this.validateKey);
      for (const rule of rules) {
        const ok = NumberInput.applyRule(rule, n);
        if (!ok) { isValid = false; break; }
      }
    }

    return isValid ? this._setValid() : this._setInvalid();
  }


  // ---------- Static helpers & rules ----------
  /**
   * Function: parseNumber
   * Purpose: Parse numbers with decimal comma support.
   * Params:
   *   v (string) - raw input value
   * Returns:
   *   number|null
   */
  static parseNumber(v) {
    if (typeof v !== 'string') return Number.isFinite(v) ? +v : null;
    const s = v.trim();
    if (s === '') return null;
    const norm = s.replace(',', '.');
    const n = Number(norm);
    return Number.isFinite(n) ? n : null;
  }

  /**
   * Function: parseValidateKey
   * Purpose: Parse data-validate string into structured rules.
   */
  static parseValidateKey(key) {
    const parts = key.split(/[,\s]+/).filter(Boolean);
    return parts.map(p => {
      const [t, argStr] = p.split(':');
      const args = (argStr ?? '')
        .split(',')
        .filter(Boolean)
        .map(NumberInput.parseNumber)
        .filter(x => Number.isFinite(x));
      return { type: t, args };
    });
  }

  /**
   * Function: applyRule
   * Purpose: Apply a numeric rule to value n.
   */
  static applyRule(rule, n) {
    switch (rule.type) {
      case 'integer':      return Number.isInteger(n);
      case 'positive':     return n > 0;
      case 'nonNegative':  return n >= 0;
      case 'min':          return rule.args.length ? n >= rule.args[0] : true;
      case 'max':          return rule.args.length ? n <= rule.args[0] : true;
      case 'range':        return rule.args.length >= 2 ? (n >= rule.args[0] && n <= rule.args[1]) : true;
      case 'step': {
        if (!rule.args.length) return true;
        const step = rule.args[0];
        if (!(step > 0)) return true;
        const base = 0;
        const diff = Math.abs((n - base) / step);
        return Math.abs(diff - Math.round(diff)) <= 1e-9;
      }
      default: return true;
    }
  }
}
